import React from 'react';
import { noop } from 'lodash';

import GetBusinessSuggestions from '@src/api/GetBusinessSuggestions';
import SearchBusinesses from '@src/api/SearchBusinesses';
import { generateBusiness } from '@src/factories';
import { getUserLocation } from '@src/helpers';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useAxiosRequestConfig from '@src/hooks/useAxiosRequestConfig';
import useDelayedState from '@src/hooks/useDelayedState';
import useIsMounted from '@src/hooks/useIsMounted';
import { Business } from '@src/types';

type GeoPoint = {
  latitude: number;
  longitude: number;
};

const useLocations = () => {
  const axiosRequestConfig = useAxiosRequestConfig();
  const [location, setLocation] = React.useState<GeoPoint>();
  const [search, setSearch] = React.useState<string>();
  const [ended, setEnded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [data, setData] = React.useState<Business[]>([]);
  const query = useDelayedState(search, 500);
  const axiosErrorHandler = useAxiosErrorHandler();
  const isMounted = useIsMounted();
  const limit = 12;
  const nearby = true;
  const pageSize = limit;

  const makeRequest = React.useCallback(
    async (_offset: number, _limit = limit): Promise<Business[]> => {
      const page = Math.floor(_offset / pageSize);

      if (location && !query) {
        const response = await GetBusinessSuggestions(
          location.latitude,
          location.longitude,
          page,
          nearby,
          undefined,
          axiosRequestConfig
        );
        return response.data.results.map((business) =>
          generateBusiness(business)
        );
      } else if (query) {
        const response = await SearchBusinesses(
          query,
          page,
          axiosRequestConfig
        );
        return response.data.results.map((business) =>
          generateBusiness(business)
        );
      }

      return [];
    },
    [axiosRequestConfig, location, nearby, pageSize, query]
  );

  const handleRefresh = React.useCallback(async () => {
    try {
      setRefreshing(true);
      setEnded(false);
      const newData = await makeRequest(0, limit);
      setData(newData);
      setOffset(limit);
      setRefreshing(false);
    } catch (e) {
      if (isMounted) {
        setRefreshing(false);
      }
      axiosErrorHandler(e);
    }
  }, [axiosErrorHandler, isMounted, limit, makeRequest]);

  const handleEndReached = React.useCallback(async () => {
    if (loading || ended) {
      return;
    }
    try {
      setLoading(true);
      const newData = await makeRequest(offset, limit);
      if (newData.length === 0) {
        setEnded(true);
        setData([...data]);
      } else {
        setData([...data, ...newData]);
        setOffset(limit + offset);
      }
    } catch (e) {
      axiosErrorHandler(e);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [axiosErrorHandler, data, ended, isMounted, loading, makeRequest, offset]);

  React.useEffect(() => {
    handleRefresh().catch(noop);
  }, [handleRefresh]);

  React.useEffect(() => {
    async function getLocation() {
      const res = await getUserLocation(true);
      if (res) {
        setLocation({ ...res });
      }
    }

    getLocation().catch(noop);
  }, []);

  // React.useEffect(() => {
  //   handleRefresh().catch(noop);
  // }, [handleRefresh, query]);

  return React.useMemo(
    () => ({
      data,
      handleEndReached,
      handleRefresh,
      refreshing,
      loading,
      search,
      setSearch,
    }),
    [data, handleEndReached, handleRefresh, loading, refreshing, search]
  );
};

export default useLocations;
